import { useUnitNames } from '../../facilities/index.js'
import { Check, RotateCcw, X } from 'lucide-react'
import { Button, EndpointHint, FormError, Skeleton } from '../../core/index.js'
import { useCensusDecisions, useCensusEvaluations, useScheduleBoard } from '../hooks.js'
import type { CensusEvaluationRecord, HeldSeat } from '../types.js'
import { shortDay } from './censusCardData.js'
import { formatWhen } from './boardText.js'
import { SHIFT_TYPE_LABELS } from './preferencesText.js'

/**
 * What the engine did with the census (census response plan F2): every
 * evaluation made as a count was recorded - applied to the month, proposed
 * for a person (Apply / Dismiss), dismissed or noted - and the seats it is
 * holding, each with Offer back. Read-only without `facilityId` for the
 * decisions (the admin's dialog).
 */

const STATUS_CLASS: Record<CensusEvaluationRecord['status'], string> = { applied: 'bg-coral/10 text-coral', proposed: 'bg-gold/30 text-ink', dismissed: 'bg-marine/10 text-ink-mid', noted: 'bg-marine/10 text-ink-mid' }
const STATUS_TEXT: Record<CensusEvaluationRecord['status'], string> = { applied: 'Applied', proposed: 'Proposed', dismissed: 'Dismissed', noted: 'Noted' }
const HELD_TEXT: Record<NonNullable<CensusEvaluationRecord['heldBecause']>, string> = { mode_propose: 'the facility applies by hand', inside_notice: 'less notice than the facility gives a worker', inside_lock: 'the shift starts too soon for the engine to change it - a Clinical Bridge callout from the Today tab fills a gap this close', rise_not_applied_yet: 'posting the seats was the next phase' }
const SEAT_STATUS_TEXT: Record<HeldSeat['status'], string> = { held: 'Held', recall_offered: 'Offered back', recalled: 'Taken back', released: 'On the open board' }

function describeRecord(record: CensusEvaluationRecord, units: ReadonlyMap<string, string>): string {
  const { evaluation } = record
  const basis = record.censusBasis === 'given' ? 'tried' : record.censusBasis === 'recorded' ? 'recorded' : 'forecast'
  if (evaluation.verdict === 'fall') {
    return `${evaluation.releases.map((release) => `${release.worker.name} ${release.action === 'float' ? `floats to ${release.floatTo ? (units.get(release.floatTo.unitId) ?? release.floatTo.unitId) : 'another unit'}` : 'released'} (${SHIFT_TYPE_LABELS[release.shiftType].toLowerCase()})`).join('; ')} · census ${evaluation.census} ${basis}`
  }
  if (evaluation.verdict === 'rise') {
    return `${evaluation.additions.map((addition) => `${addition.seats} ${addition.role} ${SHIFT_TYPE_LABELS[addition.shiftType].toLowerCase()}`).join(', ')} to post · census ${evaluation.census} ${basis}`
  }
  return evaluation.verdict === 'no_change' ? `nothing to change · census ${evaluation.census} ${basis}` : 'nothing to judge'
}

export function CensusEvaluationList({ records, canDecide, facilityId }: { records: readonly CensusEvaluationRecord[]; canDecide: boolean; facilityId: string }) {
  const decisions = useCensusDecisions(facilityId)
  const units = useUnitNames(facilityId)
  const busy = decisions.apply.isPending || decisions.dismiss.isPending
  const failure = decisions.apply.error ?? decisions.dismiss.error ?? null
  if (records.length === 0) {
    return <p className="text-[11px] text-ink-mid">Nothing yet: the engine evaluates each count recorded for a released month.</p>
  }
  return (
    <div>
      <ul className="divide-y divide-marine/10 text-[11px]">
        {records.map((record) => (
          <li key={record.evaluationId} className="flex flex-wrap items-start justify-between gap-2 py-1.5">
            <div className="min-w-0">
              <span className={`mr-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_CLASS[record.status]}`}>{STATUS_TEXT[record.status]}</span>
              <span className="font-bold text-ink">
                {shortDay(record.date)} · {record.unitId}
              </span>
              <span className="text-ink-mid"> · {describeRecord(record, units)}</span>
              <div className="text-[10px] text-ink-mid">
                {formatWhen(record.createdAt)}
                {record.heldBecause && ` · waiting: ${HELD_TEXT[record.heldBecause]}`}
                {record.applied && ` · ${[record.applied.floated > 0 ? `${record.applied.floated} floated` : null, record.applied.flexed > 0 ? `${record.applied.flexed} flexed` : null, (record.applied.recalled ?? 0) > 0 ? `${record.applied.recalled} offered back` : null, (record.applied.posted ?? 0) > 0 ? `${record.applied.posted} posted` : null].filter(Boolean).join(', ') || 'nothing to do'}`}
                {record.evaluation.hppd.scheduled !== null && ` · ${record.evaluation.hppd.scheduled.toFixed(2)} HPPD${record.evaluation.hppd.target !== null ? ` against ${record.evaluation.hppd.target.toFixed(2)}` : ''}`}
              </div>
            </div>
            {canDecide && record.status === 'proposed' && (
              <span className="flex gap-1">
                <Button icon={Check} size="sm" disabled={busy} onClick={() => decisions.apply.mutate(record.evaluationId)}>
                  Apply
                </Button>
                <Button icon={X} variant="secondary" size="sm" disabled={busy} onClick={() => decisions.dismiss.mutate(record.evaluationId)}>
                  Dismiss
                </Button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {failure && <FormError error={failure} fallback="The decision could not be recorded." />}
    </div>
  )
}

export function HeldSeatList({ held, names, canOffer, facilityId }: { held: readonly HeldSeat[]; names: ReadonlyMap<string, string>; canOffer: boolean; facilityId: string }) {
  const decisions = useCensusDecisions(facilityId)
  if (held.length === 0) {
    return null
  }
  return (
    <div className="mt-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-marine">Seats held back</div>
      <ul className="divide-y divide-marine/10 text-[11px]">
        {held.map((seat) => (
          <li key={seat.shiftId} className="flex flex-wrap items-center justify-between gap-2 py-1">
            <span>
              <span className="font-bold text-ink">{names.get(seat.workerId) ?? seat.workerId}</span> · {shortDay(seat.date)} {SHIFT_TYPE_LABELS[seat.shiftType].toLowerCase()} on {seat.unitId} · {SEAT_STATUS_TEXT[seat.status]}
              {(seat.status === 'held' || seat.status === 'recall_offered') && <span className="text-ink-mid"> · first right back until {formatWhen(seat.recallUntil)}</span>}
            </span>
            {canOffer && seat.status === 'held' && (
              <Button icon={RotateCcw} variant="secondary" size="sm" disabled={decisions.offerRecall.isPending} onClick={() => decisions.offerRecall.mutate(seat.shiftId)}>
                Offer back
              </Button>
            )}
          </li>
        ))}
      </ul>
      {decisions.offerRecall.isError && <FormError error={decisions.offerRecall.error} fallback="The seat could not be offered back." />}
    </div>
  )
}

/** The Census tool's section: this month's and next month's evaluations, and the released month's held seats. */
export function CensusDecisions({ facilityId, thisMonth, nextMonth, canDecide }: { facilityId: string; thisMonth: string; nextMonth: string; canDecide: boolean }) {
  const current = useCensusEvaluations(facilityId, thisMonth)
  const next = useCensusEvaluations(facilityId, nextMonth)
  const boardThis = useScheduleBoard(facilityId, thisMonth)
  const boardNext = useScheduleBoard(facilityId, nextMonth)
  const records = [...(next.data ?? []), ...(current.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const boards = [boardThis.data, boardNext.data].filter((board): board is NonNullable<typeof board> => Boolean(board))
  const names = new Map(boards.flatMap((board) => board.schedule.map((entry) => [entry.workerId, entry.name] as const)))
  const held = boards.flatMap((board) => board.held ?? [])
  return (
    <section>
      <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-marine">
        What the engine did
        <EndpointHint>GET /facilities/{'{facilityId}'}/schedule/census/evaluations?period</EndpointHint>
      </div>
      <p className="mb-2 text-[11px] text-ink-mid">Every count recorded for a released month is evaluated. A fall is applied at once where the facility runs automatic and the shift is not too close - the lowest tier floated or flexed first - and a rise the same way: a held seat offered back to its worker first, the rest posted on the open board. Otherwise it is proposed here for you to apply or dismiss. Every hour the clock releases held seats whose recall ran out and looks at the week ahead again.</p>
      {current.isPending || next.isPending ? <Skeleton /> : <CensusEvaluationList records={records} canDecide={canDecide} facilityId={facilityId} />}
      <HeldSeatList held={held} names={names} canOffer={canDecide} facilityId={facilityId} />
    </section>
  )
}
